import React, { useState } from "react";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { AlertTriangle, Upload, FileSearch, User, ShoppingBag, Music, ShieldAlert, CheckCircle2, XCircle, FileText, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCEPT = "audio/*,video/*,.mp3,.wav,.flac,.m4a,.aac,.ogg,.mp4,.mov,.webm,.mkv";

const AdminLeakDetector = () => {
  const { token, user } = useAuth();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  if (!user?.is_admin) {
    return (
      <div className="max-w-lg mx-auto py-24 px-6 text-center">
        <ShieldAlert className="w-10 h-10 text-[#EC008C] mx-auto mb-3" />
        <h1 className="font-['Unbounded'] text-2xl text-white font-bold mb-2">Admin only</h1>
        <p className="text-[#8A92A0]">The Leak Detector is restricted to platform admins.</p>
      </div>
    );
  }

  const investigate = async () => {
    if (!file) {
      toast.error("Choose a suspect audio or video file first.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/security/leak-detect`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(res.data);
      if (res.data.marker_recovered) {
        toast.success(`Marker recovered: ${res.data.marker}`);
      } else {
        toast.warning("No StardomFans marker found in this file.");
      }
    } catch (e) {
      const raw = e.response?.data?.detail;
      const msg = raw && typeof raw === "object" ? raw.message : raw || "Leak detector failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const exportEvidencePdf = async () => {
    if (!result?.marker) {
      toast.error("No marker to export");
      return;
    }
    setExporting(true);
    try {
      const res = await axios.get(
        `${API}/admin/security/leak-report/${encodeURIComponent(result.marker)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leak_evidence_${result.marker}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Evidence packet downloaded");
    } catch (e) {
      const raw = e.response?.data;
      let msg = "Evidence export failed";
      if (raw instanceof Blob) {
        try {
          const text = await raw.text();
          const parsed = JSON.parse(text);
          msg = parsed?.detail?.message || parsed?.detail || msg;
        } catch { /* ignore */ }
      } else if (raw?.detail) {
        msg = typeof raw.detail === "object" ? raw.detail.message : raw.detail;
      }
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-4" data-testid="leak-detector-page">
      <AdminNavigation />
      <div className="flex items-center gap-3 mb-2">
        <FileSearch className="w-6 h-6 text-[#EC008C]" />
        <h1 className="font-['Unbounded'] text-3xl font-bold text-white">Leak Detector</h1>
      </div>
      <p className="text-[#8A92A0] mb-8 text-sm">
        Upload any suspect audio or video file. We&apos;ll extract the buyer marker embedded at
        download-time and return the exact buyer + order + product for one-click prosecution.
      </p>

      <div className="card p-6 border border-[#1A2030] rounded-xl bg-[#0A0E18] mb-6">
        <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#EC008C]/30 rounded-lg py-10 cursor-pointer hover:border-[#EC008C]/60 transition-colors" data-testid="leak-file-drop">
          <Upload className="w-10 h-10 text-[#EC008C] mb-3" />
          <span className="text-white font-semibold">
            {file ? file.name : "Drop or choose a file"}
          </span>
          <span className="text-xs text-[#8A92A0] mt-1">
            mp3 · wav · flac · m4a · mp4 · mov · webm · mkv (max 500 MB)
          </span>
          <input
            type="file"
            className="hidden"
            accept={ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="leak-file-input"
          />
        </label>

        <Button
          onClick={investigate}
          disabled={busy || !file}
          className="w-full mt-4 bg-[#EC008C] text-black font-semibold h-11 hover:bg-[#FF3399]"
          data-testid="leak-investigate-btn"
        >
          {busy ? "Investigating…" : "Extract buyer marker"}
        </Button>
      </div>

      {result && (
        <div
          className={`card p-6 rounded-xl border ${
            result.marker_recovered
              ? "border-red-500/40 bg-red-500/[0.04]"
              : "border-[#1A2030] bg-[#0A0E18]"
          }`}
          data-testid="leak-result"
        >
          <div className="flex items-center gap-2 mb-3">
            {result.marker_recovered ? (
              <>
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="font-['Unbounded'] text-lg font-bold text-red-300">
                  LEAK DETECTED
                </span>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-[#8A92A0]" />
                <span className="font-['Unbounded'] text-lg font-bold text-white">
                  No marker recovered
                </span>
              </>
            )}
          </div>

          <p className="text-[#c0c9d4] text-sm mb-4">{result.note}</p>

          {result.marker_recovered && (
            <div className="space-y-3">
              <Field label="Buyer Marker" value={result.marker} mono />
              <Field label="Recovery Channel" value={result.detection_channel || "—"} />
              <Field label="Media Type" value={result.media_type} />

              {result.buyer ? (
                <div className="rounded-lg border border-[#EC008C]/40 bg-[#EC008C]/[0.06] p-4 space-y-2" data-testid="leak-buyer-card">
                  <div className="flex items-center gap-2 text-[#EC008C] font-bold">
                    <User className="w-4 h-4" /> Original Buyer
                  </div>
                  <Field label="Email" value={result.buyer.email} />
                  <Field label="Name" value={result.buyer.name || "—"} />
                  <Field label="Country" value={result.buyer.country || "—"} />
                  <Field label="KYC" value={result.buyer.kyc_status || "—"} />
                  <Field label="User ID" value={result.buyer.user_id} mono />
                </div>
              ) : (
                <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/[0.06] p-4 text-sm text-yellow-200">
                  Marker was recovered but no matching `watermark_receipts` row was found — this
                  can happen if the receipt was archived. Search the ledger by marker for the
                  original transaction.
                </div>
              )}

              {result.order && (
                <div className="rounded-lg border border-[#1A2030] bg-[#0A0E18] p-4 space-y-2" data-testid="leak-order-card">
                  <div className="flex items-center gap-2 text-white font-bold">
                    <ShoppingBag className="w-4 h-4" /> Order Details
                  </div>
                  <Field label="Order ID" value={result.order.order_id} mono />
                  <Field label="Amount" value={`${(result.order.amount_cents || 0) / 100} Tokens`} />
                  <Field label="Tax" value={`${(result.order.tax_cents || 0) / 100} Tokens`} />
                  <Field
                    label="Total paid"
                    value={`${(result.order.total_paid_cents || result.order.amount_cents || 0) / 100} Tokens`}
                  />
                  <Field label="Purchased at" value={result.order.created_at || ""} />
                </div>
              )}

              {result.product && (
                <div className="rounded-lg border border-[#1A2030] bg-[#0A0E18] p-4 space-y-2" data-testid="leak-product-card">
                  <div className="flex items-center gap-2 text-white font-bold">
                    <Music className="w-4 h-4" /> Product
                  </div>
                  <Field label="Name" value={result.product.name} />
                  <Field label="Category" value={result.product.category} />
                  <Field label="Artist" value={result.artist?.stage_name || result.product.artist_id} />
                </div>
              )}

              <div className="rounded-lg border border-green-500/40 bg-green-500/[0.05] p-4 text-sm text-green-200 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  This finding is now logged in the security threat ledger as
                  <span className="font-mono mx-1 text-white">leak_detected</span>
                  with a signed row and a critical admin inbox alert. Export the row for
                  prosecution from
                  <span className="font-mono mx-1 text-white">/admin/security/threats</span>.
                </span>
              </div>

              <Button
                onClick={exportEvidencePdf}
                disabled={exporting}
                className="w-full mt-2 bg-white text-black font-semibold h-11 hover:bg-gray-100"
                data-testid="leak-evidence-pdf-btn"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Building evidence packet…
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Print evidence packet (signed PDF)
                  </>
                )}
              </Button>
              <p className="text-[10px] text-[#8A92A0] text-center -mt-1">
                Hand this straight to law enforcement — HMAC-signed with QR verification.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, mono = false }) => (
  <div className="flex items-start gap-3 text-sm">
    <span className="text-[#8A92A0] w-32 flex-shrink-0">{label}</span>
    <span className={`text-white break-all ${mono ? "font-mono text-xs" : ""}`}>{value ?? "—"}</span>
  </div>
);

export default AdminLeakDetector;
